import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./SubscriptionSuccess.css";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * /subscription/success — landing page after a completed Paddle checkout.
 * Polls the entitlement endpoint briefly so the user sees their upgraded
 * tier as soon as the Paddle webhook lands (usually a few seconds).
 */
export default function SubscriptionSuccess() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [activated, setActivated] = useState(false);
  const [checking, setChecking] = useState(true);

  const plan = params.get("plan") === "enterprise" ? "Enterprise" : "Pro";

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API_BASE}/api/billing/entitlement`, { credentials: "include" });
        if (res.ok) {
          const j = await res.json();
          if (!cancelled && (j.isPro || j.isEnterprise || j.isOwner)) {
            setActivated(true);
            setChecking(false);
            return;
          }
        }
      } catch (_) { /* keep polling */ }
      if (!cancelled) {
        if (attempts < 10) setTimeout(poll, 3000);
        else setChecking(false);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="subscription-success-page" className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-2xl border border-emerald-500/30 bg-neutral-900 p-8 text-center rsc-subsuccess-card">
        <div className="text-5xl mb-4">🎉</div>
        <h1 className="text-2xl font-semibold mb-3" data-testid="subscription-success-heading">
          Thank you for subscribing!
        </h1>
        <p className="text-neutral-400 text-sm mb-6" data-testid="subscription-success-plan-note">
          Your RigSentinel {plan} subscription is being activated.
        </p>

        {activated ? (
          <div data-testid="subscription-success-activated" className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-sm py-3 px-4 mb-6">
            ✅ Your account has been upgraded. All features are now unlocked.
          </div>
        ) : checking ? (
          <div data-testid="subscription-success-pending" className="rounded-lg border border-amber-400/40 bg-amber-500/10 text-amber-200 text-sm py-3 px-4 mb-6 inline-flex items-center justify-center gap-2 w-full">
            <span className="rsc-subsuccess-spinner" aria-hidden="true" />
            Confirming your subscription… this usually takes a few seconds.
          </div>
        ) : (
          <div data-testid="subscription-success-delayed" className="rounded-lg border border-neutral-700 bg-neutral-800 text-neutral-300 text-sm py-3 px-4 mb-6">
            Your payment was received. If your account isn't upgraded within a
            few minutes, sign out and back in, or contact rigsentinel@gmail.com.
          </div>
        )}

        <button
          type="button"
          data-testid="subscription-success-go-dashboard"
          onClick={() => nav("/home")}
          className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-semibold py-3 transition"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}
